import { useEffect, useRef, useState } from 'react';
import { ArrowRight, Check, ExternalLink, RotateCcw } from 'lucide-react';
import { Link } from 'react-router-dom';
import { AnomalyEvent, AnomalyLabel, SaveReview } from './types';
import { dateTime, resolveLabel } from './helpers';
import EventChart from './Chart';
import { baselineLabels } from './Statistics';

export default function EventEditor({
  event,
  busy,
  labels,
  onSave,
  onSaved,
  onDirtyChange,
  onBusyChange,
}: {
  event: AnomalyEvent;
  busy: boolean;
  labels: AnomalyLabel[];
  onSave: (command: SaveReview) => Promise<AnomalyEvent>;
  onSaved: (next: boolean) => void;
  onDirtyChange: (dirty: boolean) => void;
  onBusyChange: (busy: boolean) => void;
}) {
  const [base, setBase] = useState(event.review);
  const [labelId, setLabelId] = useState(resolveLabel(event.review.labelId, labels)?.id || '');
  const [name, setName] = useState('');
  const [note, setNote] = useState(event.review.note);
  const [showNote, setShowNote] = useState(Boolean(event.review.note));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const labelInput = useRef<HTMLInputElement>(null);
  const existing = labels.filter((label) => !label.archived && !label.mergedInto);
  const options = existing.filter((label) =>
    label.name.toLocaleLowerCase('da-DK').includes(name.trim().toLocaleLowerCase('da-DK'))
  );
  const exact = existing.find(
    (label) =>
      label.name.toLocaleLowerCase('da-DK') ===
      name.trim().replace(/\s+/g, ' ').toLocaleLowerCase('da-DK')
  );
  const creating = Boolean(name.trim() && !exact);
  const effectiveId = name.trim() ? exact?.id || '' : labelId;
  const dirty =
    note !== base.note ||
    labelId !== (resolveLabel(base.labelId, labels)?.id || '') ||
    Boolean(name);
  useEffect(() => {
    onDirtyChange(dirty);
  }, [dirty, onDirtyChange]);
  useEffect(() => {
    labelInput.current?.focus({ preventScroll: true });
  }, []);
  async function save(status: SaveReview['status'], next: boolean) {
    if (saving || busy) return;
    setSaving(true);
    onBusyChange(true);
    setError('');
    try {
      const result = await onSave({
        eventId: event.id,
        expectedVersion: base.version,
        status,
        labelId: effectiveId || null,
        ...(creating ? { newLabel: name.trim() } : {}),
        note,
      });
      setBase(result.review);
      setLabelId(result.review.labelId || '');
      setNote(result.review.note);
      setName('');
      onDirtyChange(false);
      onSaved(next);
    } catch (cause) {
      setError((cause as Error).message || 'Kunne ikke gemme. Din annotation er bevaret.');
    } finally {
      setSaving(false);
      onBusyChange(false);
    }
  }
  const stale = event.review.version !== base.version;
  return (
    <section className="anomaly-detail" aria-label="Valgt afvigelse">
      <header className="anomaly-detail-header">
        <div>
          <span className="anomaly-eyebrow">
            {event.orgTitle} / {event.sensorTitle}
          </span>
          <h2>{event.title}</h2>
          <p>
            {dateTime(event.occurredAt)} ·{' '}
            {event.deviceFamily === 'FLOW' ? 'Flow / niveau' : 'GSM anlæg'}
          </p>
        </div>
        <Link to={event.detailPath} aria-label="Åbn sensor" title="Åbn sensor">
          <ExternalLink size={18} />
        </Link>
      </header>
      <div className="anomaly-evidence">
        <EventChart event={event} />
        <p>{event.description}</p>
        {event.assessment && <p className="anomaly-muted">Grundlag: {baselineLabels[event.assessment.baselineSource]} · {event.assessment.historyCount} tidligere perioder · {event.assessment.contextSource === 'pump' ? 'drift fra pumpetid' : event.assessment.contextSource === 'schedule' ? 'forventet drift fra plan' : 'drift ukendt'} · indstillinger v{event.assessment.profileVersion}. Dette er grundlaget fra registreringstidspunktet.</p>}
      </div>
      <form
        className="anomaly-annotation"
        onSubmit={(e) => {
          e.preventDefault();
          if (effectiveId || creating) void save('reviewed', true);
        }}
      >
        <fieldset disabled={saving || busy}>
          <legend>Hvad skete der?</legend>
          <p className="anomaly-muted">Vælg en forklaring, eller skriv en ny label.</p>
          {base.status !== 'pending' && (
            <p className="anomaly-review-credit">
              <Check size={14} /> {base.status === 'reviewed' ? 'Vurderet' : 'Under undersøgelse'}{' '}
              af {base.updatedByName || 'en kollega'}
              {base.updatedAt ? ` · ${dateTime(base.updatedAt)}` : ''}
            </p>
          )}
          <label className="anomaly-field-label" htmlFor="anomaly-label-search">
            Forklaring
          </label>
          <input
            ref={labelInput}
            id="anomaly-label-search"
            className="anomaly-input"
            placeholder="Søg eller opret label…"
            maxLength={60}
            value={name}
            onChange={(e) => setName(e.target.value)}
            aria-describedby="anomaly-label-selection"
          />
          <div className="anomaly-label-options" role="group" aria-label="Genbrug en label">
            {options.slice(0, name ? 30 : 8).map((label) => (
              <button
                type="button"
                key={label.id}
                className={`anomaly-label-chip ${effectiveId === label.id ? 'selected' : ''}`}
                aria-pressed={effectiveId === label.id}
                onClick={() => {
                  setLabelId(label.id);
                  setName('');
                }}
              >
                {effectiveId === label.id && <Check size={13} />}
                {label.name}
              </button>
            ))}
            {!existing.length && !name && (
              <span className="anomaly-muted">
                Ingen labels endnu. Skriv din første forklaring ovenfor.
              </span>
            )}
          </div>
          <p id="anomaly-label-selection" className="anomaly-selection" aria-live="polite">
            {creating
              ? `Opret “${name.trim()}” og brug ved gem`
              : effectiveId
              ? `Valgt: ${resolveLabel(effectiveId, labels)?.name || 'Label'}`
              : 'Vælg en label for at markere som vurderet.'}
          </p>
          {showNote ? (
            <div>
              <label className="anomaly-field-label" htmlFor="anomaly-note">
                Note <span>· valgfri</span>
              </label>
              <textarea
                id="anomaly-note"
                className="anomaly-input"
                rows={2}
                placeholder="Fx beholder skiftet ved servicebesøg…"
                maxLength={2000}
                value={note}
                onChange={(e) => setNote(e.target.value)}
              />
            </div>
          ) : (
            <button type="button" className="anomaly-add-note" onClick={() => setShowNote(true)}>
              + Tilføj note
            </button>
          )}
          {stale && (
            <div className="anomaly-warning" role="alert">
              Annotationen er ændret siden du åbnede den. Kopiér evt. din note og{' '}
              <button
                type="button"
                onClick={() => {
                  setBase(event.review);
                  setNote(event.review.note);
                  setShowNote(Boolean(event.review.note));
                  setLabelId(resolveLabel(event.review.labelId, labels)?.id || '');
                  setName('');
                  setError('');
                }}
              >
                hent den seneste annotation
              </button>
              .
            </div>
          )}
          {error && (
            <p className="anomaly-error" role="alert">
              {error}
            </p>
          )}
          <div className="anomaly-save-actions">
            <button
              type="submit"
              className="anomaly-button primary"
              disabled={(!effectiveId && !creating) || stale}
            >
              {saving ? 'Gemmer…' : 'Gem og næste'}
              <ArrowRight size={16} />
            </button>
            <button
              type="button"
              className="anomaly-button"
              disabled={(!effectiveId && !creating) || stale}
              onClick={() => void save('reviewed', false)}
            >
              Gem
            </button>
          </div>
          <div className="anomaly-secondary-actions">
            <button type="button" disabled={stale} onClick={() => void save('investigating', true)}>
              Ukendt · undersøg senere
            </button>
            {base.status !== 'pending' && (
              <button type="button" disabled={stale} onClick={() => void save('pending', true)}>
                <RotateCcw size={13} /> Tilbage til vurdering
              </button>
            )}
          </div>
        </fieldset>
      </form>
    </section>
  );
}
